'use client';

import dynamic from 'next/dynamic';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, Brain, FlaskConical, ImagePlus, Pencil, Trophy } from 'lucide-react';
import supabase from '@/lib/supabase';
import MarkdownComponent from '@/components/Markdown/MarkdownComponent';
import type { BoardEntry, StoredProblem } from '@/components/whiteboard-v2/types';
import type { DemoSelection } from '@/components/whiteboard-v2/LiveWhiteboard';
import { normalizeLiveTutorSubject } from '@/lib/liveTutorSubject';
import useAuthStore from '@/store/useAuthStore';
import useSubscriptionStore from '@/store/useSubscriptionStore';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import PaywallModal from '@/components/Subscription/PaywallModal';

const LiveWhiteboard = dynamic(() => import('@/components/whiteboard-v2/LiveWhiteboard'), {
  ssr: false,
  loading: () => <div className="w-full h-[560px] bg-gray-100 rounded-lg animate-pulse" />,
});

const PREVIEW_BOARD_ENTRIES: BoardEntry[] = [
  { id: 'preview-2', content: 'Expand Each Factor', renderedContent: 'Expand Each Factor', type: 'subheading', timestamp: 0 },
  { id: 'preview-1a', content: 'Power of a product rule: (ab)^n = a^n * b^n', renderedContent: '$\\text{Power of a product: }(ab)^n = a^n \\cdot b^n$', type: 'concept', timestamp: 0 },
  { id: 'preview-1b', content: 'Power of a power rule: (a^m)^n = a^(mn)', renderedContent: '$\\text{Power of a power: }(a^m)^n = a^{mn}$', type: 'concept', timestamp: 0 },
  { id: 'preview-3', content: '(3x^2)^4 = 3^4 * (x^2)^4 = 81x^8', renderedContent: '$(3x^2)^4 = 3^4 \\cdot (x^2)^4 = 81x^8$', type: 'step', timestamp: 0 },
  { id: 'preview-4', content: '(2x^4)^2 = 2^2 * (x^4)^2 = 4x^8', renderedContent: '$(2x^4)^2 = 2^2 \\cdot (x^4)^2 = 4x^8$', type: 'step', timestamp: 0 },
  { id: 'preview-5', content: 'Multiply', renderedContent: 'Multiply', type: 'subheading', timestamp: 0 },
  { id: 'preview-5b', content: 'Product of powers rule: a^m * a^n = a^(m+n)', renderedContent: '$\\text{Product of powers: }a^m \\cdot a^n = a^{m+n}$', type: 'concept', timestamp: 0 },
  { id: 'preview-6a', content: '81x^8 * 4x^8 = (81*4)(x^8 * x^8) = 324x^{16}', renderedContent: '$81x^8 \\cdot 4x^8 = (81 \\cdot 4)(x^8 \\cdot x^8) = \\boxed{324x^{16}}$', type: 'step', timestamp: 0 },
];

interface LiveTutorExperienceProps {
  isGuestSessionLocked?: boolean;
  onRequireAuth?: () => void;
  onProblemCompleted?: () => void;
  onSessionEnd?: () => void;
  desktopWidth?: number;
  desktopHeight?: number;
  skipQuota?: boolean;
}

type AutoRotatingLiveTutorTab = 'problems' | 'skills' | 'amc8' | 'history' | 'science';
type LiveTutorTab = AutoRotatingLiveTutorTab | 'upload';

const AUTO_ROTATING_LIVE_TUTOR_TABS = [
  { key: 'problems' as const, label: 'Practice', icon: Pencil, color: '#5DBB63' },
  { key: 'skills' as const, label: 'Math Skills', icon: Brain, color: '#F59E0B' },
  { key: 'amc8' as const, label: 'AMC-8 Problems', icon: Trophy, color: '#3B82F6' },
  { key: 'history' as const, label: 'Social Studies', icon: BookOpen, color: '#D97706' },
  { key: 'science' as const, label: 'Science', icon: FlaskConical, color: '#06B6D4' },
];

const UPLOAD_LIVE_TUTOR_TAB = {
  key: 'upload' as const,
  label: 'Your Problem',
  icon: ImagePlus,
  color: '#8B8CFD',
};

const MAX_HOME_UPLOAD_IMAGE_BYTES = 10 * 1024 * 1024;

function isSupportedHomeUploadImage(file: File): boolean {
  return file.type.startsWith('image/') || /\.(heic|heif|jpg|jpeg|png|gif|webp)$/i.test(file.name);
}

export default function LiveTutorExperience({
  isGuestSessionLocked = false,
  onRequireAuth,
  onProblemCompleted,
  onSessionEnd,
  desktopWidth = 700,
  desktopHeight = 478,
  skipQuota = false,
}: LiveTutorExperienceProps) {
  const { user } = useAuthStore();
  const { consumeQuota } = useSubscriptionStore();
  const { canUse } = useFeatureGate('live_tutor');
  const [showPaywall, setShowPaywall] = useState(false);

  const [liveTutorProblems, setLiveTutorProblems] = useState<StoredProblem[]>([]);
  const [isLoadingProblems, setIsLoadingProblems] = useState(true);
  const [liveTutorSkills, setLiveTutorSkills] = useState<{ skill_id: string; title: string }[]>([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);
  const [amcProblems, setAmcProblems] = useState<StoredProblem[]>([]);
  const [isLoadingAmcProblems, setIsLoadingAmcProblems] = useState(true);
  const [historyProblems, setHistoryProblems] = useState<StoredProblem[]>([]);
  const [isLoadingHistoryProblems, setIsLoadingHistoryProblems] = useState(true);
  const [scienceProblems, setScienceProblems] = useState<StoredProblem[]>([]);
  const [isLoadingScienceProblems, setIsLoadingScienceProblems] = useState(true);
  const [liveTutorActiveTab, setLiveTutorActiveTab] = useState<LiveTutorTab>('problems');
  const [isTabAutoRotating, setIsTabAutoRotating] = useState(true);
  const [liveTutorSelection, setLiveTutorSelection] = useState<DemoSelection | null>(null);
  const [homeUploadText, setHomeUploadText] = useState('');
  const [homeUploadFile, setHomeUploadFile] = useState<File | null>(null);
  const [homeUploadFileName, setHomeUploadFileName] = useState<string | null>(null);
  const [isHomeUploadMenuOpen, setIsHomeUploadMenuOpen] = useState(false);
  const [isHomeParseLoading, setIsHomeParseLoading] = useState(false);
  const [homeParseErrorMessage, setHomeParseErrorMessage] = useState<string | null>(null);
  const [isHomeParseErrorOpen, setIsHomeParseErrorOpen] = useState(false);
  const [liveTutorViewportWidth, setLiveTutorViewportWidth] = useState(1200);
  const [liveTutorStartSignal, setLiveTutorStartSignal] = useState(0);
  const carouselScrollRef = useRef<HTMLDivElement | null>(null);
  const homeUploadInputRef = useRef<HTMLInputElement | null>(null);
  const homeUploadMenuRef = useRef<HTMLDivElement | null>(null);
  const autoRotationResumeTimerRef = useRef<number | null>(null);
  const carouselDragRef = useRef<{ startX: number; startScroll: number } | null>(null);
  const didCarouselDragRef = useRef(false);
  const lastCarouselTabRef = useRef<LiveTutorTab | null>(null);
  const lastCarouselDataLenRef = useRef(0);
  const skillKeyPropertyCache = useRef<
    Map<string, { id: string; title: string; content: string; examples?: string; explanation?: string; picture?: string; picturePosition?: 'below' | 'right' }>
  >(new Map());

  const formatProblemLabel = (question: string | undefined) =>
    (question || '').replace(/^Solve\s*for\s*x\s*[:：]\s*/i, '').trim();

  const liveTutorMathSkills = useMemo(() => {
    if (liveTutorProblems.length === 0 || liveTutorSkills.length === 0) {
      return [];
    }
    if (liveTutorSkills.length >= liveTutorProblems.length) {
      return liveTutorSkills.slice(0, liveTutorProblems.length);
    }
    return Array.from({ length: liveTutorProblems.length }, (_, index) => liveTutorSkills[index % liveTutorSkills.length]);
  }, [liveTutorProblems.length, liveTutorSkills]);

  const cardFontSize = liveTutorViewportWidth < 640 ? '12px' : '13px';
  const cardHeight = liveTutorViewportWidth < 640 ? 72 : 80;
  const isMobileTabViewport = liveTutorViewportWidth < 640;

  const stopAutoRotation = useCallback(() => {
    setIsTabAutoRotating(false);
    if (autoRotationResumeTimerRef.current) {
      window.clearTimeout(autoRotationResumeTimerRef.current);
    }
    autoRotationResumeTimerRef.current = window.setTimeout(() => {
      setIsTabAutoRotating(true);
      autoRotationResumeTimerRef.current = null;
    }, 10000);
  }, []);

  const requestLiveTutorStart = useCallback(() => {
    setLiveTutorStartSignal((prev) => prev + 1);
  }, []);

  /** Check and consume quota. Returns true if allowed, false if blocked (shows paywall). */
  const checkAndConsumeQuota = useCallback(async (): Promise<boolean> => {
    if (skipQuota) return true;
    // Skip quota check for guest users (they have their own localStorage-based limit)
    if (!user?.id) return true;
    const result = await consumeQuota('live_tutor', user.id);
    if (!result.allowed) {
      setShowPaywall(true);
      return false;
    }
    return true;
  }, [skipQuota, user?.id, consumeQuota]);

  const fetchLiveTutorProblems = useCallback(async () => {
    setIsLoadingProblems(true);
    try {
      const endpoints = ['whiteboard/problems.json', '/whiteboard/problems.json', 'napi/whiteboard/problems', '/napi/whiteboard/problems'];
      let resolved: StoredProblem[] | null = null;
      let firstSuccess: StoredProblem[] | null = null;

      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, { cache: 'no-store' });
          if (!response.ok) continue;
          const raw = await response.json();
          const parsed = Array.isArray(raw) ? raw : (raw?.problems ?? []);
          const normalized = Array.isArray(parsed) ? (parsed as StoredProblem[]) : [];
          if (firstSuccess === null) firstSuccess = normalized;
          if (normalized.length > 0) {
            resolved = normalized;
            break;
          }
        } catch {
          // try next endpoint
        }
      }

      const problems = (resolved ?? firstSuccess ?? []).map((problem) => ({
        ...problem,
        subject: normalizeLiveTutorSubject(problem.subject),
      }));
      setLiveTutorProblems(problems);
      if (problems.length === 0) return;
      const previewProblem = (problems.find((problem) => problem.id === 'imported-1770007445483') ?? problems[0]);
      setLiveTutorSelection({ kind: 'problem', problem: previewProblem });
    } catch (error) {
      console.error('Failed to load live tutor problems:', error);
      setLiveTutorProblems([]);
    } finally {
      setIsLoadingProblems(false);
    }
  }, []);

  const fetchLiveTutorSkills = useCallback(async () => {
    setIsLoadingSkills(true);
    try {
      const { data: configData, error: configError } = await supabase
        .from('tutor_agent_config')
        .select('value')
        .eq('key', 'whiteboard_preset_skills')
        .maybeSingle();
      if (configError) throw configError;

      const rawValue = configData?.value;
      const parsedValue = typeof rawValue === 'string'
        ? (() => { try { return JSON.parse(rawValue); } catch { return []; } })()
        : rawValue;

      const presetSkillIds = Array.from(
        new Set(
          (Array.isArray(parsedValue) ? parsedValue : Array.isArray((parsedValue as { skills?: unknown[] } | null)?.skills) ? (parsedValue as { skills: unknown[] }).skills : [])
            .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
            .map((id) => id.trim())
        )
      );
      if (presetSkillIds.length === 0) {
        setLiveTutorSkills([]);
        return;
      }

      const { data, error } = await supabase
        .from('syllabus_skill')
        .select('element_id, title, elements_json')
        .in('element_id', presetSkillIds);
      if (error) throw error;

      const skillsById = new Map(
        (data ?? [])
          .filter((item: { element_id: string; title: string | null; elements_json?: unknown }) => item.elements_json !== null)
          .map((item: { element_id: string; title: string | null }) => ({
            skill_id: item.element_id,
            title: (item.title || '').trim(),
          }))
          .filter((item) => item.title.length > 0 && !/untitled/i.test(item.title))
          .map((item) => [item.skill_id, item] as const)
      );
      const orderedSkills = presetSkillIds
        .map((id) => skillsById.get(id))
        .filter((item): item is { skill_id: string; title: string } => Boolean(item));
      setLiveTutorSkills(orderedSkills);
    } catch (error) {
      console.error('Failed to load skills:', error);
      setLiveTutorSkills([]);
    } finally {
      setIsLoadingSkills(false);
    }
  }, []);

  const fetchAmcProblems = useCallback(async () => {
    setIsLoadingAmcProblems(true);
    try {
      const response = await fetch('/napi/competitions/amc8-2026/problems');
      if (!response.ok) throw new Error('Failed to load AMC8 questions');
      const data = await response.json();
      setAmcProblems(((data.problems ?? []) as StoredProblem[]).map((problem) => ({
        ...problem,
        subject: normalizeLiveTutorSubject(problem.subject ?? 'amc_math'),
      })));
    } catch (error) {
      console.error('Failed to load AMC8 problems:', error);
      setAmcProblems([]);
    } finally {
      setIsLoadingAmcProblems(false);
    }
  }, []);

  const extractKeyProperty = useCallback((skillId: string, rawElements: unknown) => {
    const elements = Array.isArray(rawElements) ? rawElements : [];
    const keyProps = elements
      .filter((el: { element_type?: string }) => el.element_type === 'key_property')
      .map((el: { id?: string; key_property?: { title?: string; content?: string; examples?: string; explanation?: string; picture?: string; picture_position?: 'left' | 'bottom' } }, idx: number) => ({
        id: el.id || `kp-${idx}`,
        title: el.key_property?.title || `Key Property ${idx + 1}`,
        content: el.key_property?.content || '',
        examples: el.key_property?.examples,
        explanation: el.key_property?.explanation,
        picture: el.key_property?.picture,
        picturePosition: (el.key_property?.picture_position === 'left' ? 'right' : 'below') as 'below' | 'right',
      }));
    if (keyProps.length === 0) return null;
    const preferred = keyProps.find((kp) => kp.content.trim().length > 0 || kp.title.trim().length > 0);
    const selected = preferred ?? keyProps[0];
    skillKeyPropertyCache.current.set(skillId, selected);
    return selected;
  }, []);

  const fetchSkillKeyProperty = useCallback(async (skillId: string) => {
    const cached = skillKeyPropertyCache.current.get(skillId);
    if (cached) return cached;
    const { data, error } = await supabase.from('syllabus_skill').select('elements_json').eq('element_id', skillId).single();
    if (error || !data) return null;
    return extractKeyProperty(skillId, data.elements_json);
  }, [extractKeyProperty]);

  const buildSkillProblem = useCallback((skill: { skill_id: string; title: string }, keyProperty: { id: string; title: string; content: string; examples?: string; explanation?: string }): StoredProblem => {
    const explanationParts = [keyProperty.content, keyProperty.examples, keyProperty.explanation].filter(Boolean) as string[];
    return {
      id: `skill-${skill.skill_id}-${keyProperty.id}`,
      question: keyProperty.title || skill.title,
      answer: keyProperty.content || keyProperty.title || skill.title,
      explanation: explanationParts.join(' ').trim() || keyProperty.title || skill.title,
      createdAt: new Date().toISOString(),
      subject: normalizeLiveTutorSubject(undefined),
    };
  }, []);

  const handleCarouselDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    const container = carouselScrollRef.current;
    if (!container) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    carouselDragRef.current = { startX: clientX, startScroll: container.scrollLeft };
    didCarouselDragRef.current = false;
    if (liveTutorActiveTab !== 'upload') {
      stopAutoRotation();
    }
  };

  const handleCarouselDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!carouselDragRef.current) return;
    const container = carouselScrollRef.current;
    if (!container) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const delta = carouselDragRef.current.startX - clientX;
    if (Math.abs(delta) > 5) {
      didCarouselDragRef.current = true;
      container.scrollLeft = carouselDragRef.current.startScroll + delta;
    }
  };

  const handleCarouselDragEnd = () => {
    carouselDragRef.current = null;
  };

  const handleSelectLiveTutorProblem = async (problemId: string) => {
    if (didCarouselDragRef.current) {
      didCarouselDragRef.current = false;
      return;
    }
    stopAutoRotation();
    if (isGuestSessionLocked) {
      onRequireAuth?.();
      return;
    }
    const selectedProblem = liveTutorProblems.find((p) => p.id === problemId);
    if (!selectedProblem) return;
    if (!await checkAndConsumeQuota()) return;
    setLiveTutorSelection({ kind: 'problem', problem: selectedProblem });
    requestLiveTutorStart();
  };

  const handleSelectLiveTutorSkill = async (skill: { skill_id: string; title: string }) => {
    if (didCarouselDragRef.current) {
      didCarouselDragRef.current = false;
      return;
    }
    stopAutoRotation();
    if (isGuestSessionLocked) {
      onRequireAuth?.();
      return;
    }
    const keyProperty = await fetchSkillKeyProperty(skill.skill_id);
    if (!keyProperty) return;
    const skillProblem = buildSkillProblem(skill, keyProperty);
    if (!await checkAndConsumeQuota()) return;
    setLiveTutorSelection({ kind: 'skill', skillId: skill.skill_id, keyProperty, problem: skillProblem });
    requestLiveTutorStart();
  };

  const fetchHistoryProblems = useCallback(async () => {
    setIsLoadingHistoryProblems(true);
    try {
      const response = await fetch('/whiteboard/history-problems.json', { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to load history problems');
      const data = await response.json();
      setHistoryProblems(((data.problems ?? []) as StoredProblem[]).map((p) => ({ ...p, subject: normalizeLiveTutorSubject(p.subject ?? 'history') })));
    } catch (error) {
      console.error('Failed to load history problems:', error);
      setHistoryProblems([]);
    } finally {
      setIsLoadingHistoryProblems(false);
    }
  }, []);

  const fetchScienceProblems = useCallback(async () => {
    setIsLoadingScienceProblems(true);
    try {
      const response = await fetch('/whiteboard/science-problems.json', { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to load science problems');
      const data = await response.json();
      setScienceProblems(((data.problems ?? []) as StoredProblem[]).map((p) => ({ ...p, subject: normalizeLiveTutorSubject(p.subject ?? 'science') })));
    } catch (error) {
      console.error('Failed to load science problems:', error);
      setScienceProblems([]);
    } finally {
      setIsLoadingScienceProblems(false);
    }
  }, []);

  const handleSelectAmcProblem = async (problemId: string) => {
    if (didCarouselDragRef.current) {
      didCarouselDragRef.current = false;
      return;
    }
    stopAutoRotation();
    if (isGuestSessionLocked) {
      onRequireAuth?.();
      return;
    }
    const selectedProblem = amcProblems.find((p) => p.id === problemId);
    if (!selectedProblem) return;
    if (!await checkAndConsumeQuota()) return;
    setLiveTutorSelection({ kind: 'problem', problem: selectedProblem });
    requestLiveTutorStart();
  };

  const handleSelectHistoryProblem = async (problemId: string) => {
    if (didCarouselDragRef.current) { didCarouselDragRef.current = false; return; }
    stopAutoRotation();
    if (isGuestSessionLocked) { onRequireAuth?.(); return; }
    const selectedProblem = historyProblems.find((p) => p.id === problemId);
    if (!selectedProblem) return;
    if (!await checkAndConsumeQuota()) return;
    setLiveTutorSelection({ kind: 'problem', problem: selectedProblem });
    requestLiveTutorStart();
  };

  const handleSelectScienceProblem = async (problemId: string) => {
    if (didCarouselDragRef.current) { didCarouselDragRef.current = false; return; }
    stopAutoRotation();
    if (isGuestSessionLocked) { onRequireAuth?.(); return; }
    const selectedProblem = scienceProblems.find((p) => p.id === problemId);
    if (!selectedProblem) return;
    if (!await checkAndConsumeQuota()) return;
    setLiveTutorSelection({ kind: 'problem', problem: selectedProblem });
    requestLiveTutorStart();
  };

  const handleToggleHomeUploadMenu = () => setIsHomeUploadMenuOpen((prev) => !prev);

  const handlePickHomeImage = () => {
    setIsHomeUploadMenuOpen(false);
    homeUploadInputRef.current?.click();
  };

  const handleHomeUploadFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      setHomeUploadFile(null);
      setHomeUploadFileName(null);
      event.target.value = '';
      return;
    }
    if (!isSupportedHomeUploadImage(file)) {
      setHomeParseErrorMessage('Please upload an image file (JPG, PNG, GIF, WebP, or HEIC).');
      setIsHomeParseErrorOpen(true);
      event.target.value = '';
      return;
    }
    if (file.size > MAX_HOME_UPLOAD_IMAGE_BYTES) {
      setHomeParseErrorMessage('Image size must be under 10MB');
      setIsHomeParseErrorOpen(true);
      event.target.value = '';
      return;
    }

    setIsHomeParseErrorOpen(false);
    setHomeParseErrorMessage(null);

    try {
      setHomeUploadFile(file);
      setHomeUploadFileName(file.name);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      setHomeUploadFile(null);
      setHomeUploadFileName(null);
      setHomeParseErrorMessage(
        message || 'This image could not be prepared for upload. Please retry with a supported image file.'
      );
      setIsHomeParseErrorOpen(true);
    } finally {
      event.target.value = '';
    }
  };

  const handleHomeUploadSubmit = async () => {
    if (isGuestSessionLocked) {
      onRequireAuth?.();
      return;
    }
    const trimmedText = homeUploadText.trim();
    if (!trimmedText && !homeUploadFile) return;
    setIsHomeParseLoading(true);
    setHomeParseErrorMessage(null);
    setIsHomeParseErrorOpen(false);
    try {
      const formData = new FormData();
      if (trimmedText) formData.append('text', trimmedText);
      if (homeUploadFile) formData.append('image', homeUploadFile);
      const response = await fetch('/napi/dev/question-input/parse', { method: 'POST', body: formData });
      const data = (await response.json()) as { question?: string; answer?: string; explanation?: string; options?: string[]; type?: string; error?: string };
      if (!response.ok) throw new Error(data.error || 'Failed to parse question');
      const parsedProblem: StoredProblem = {
        id: `home-input-${Date.now()}`,
        question: String(data.question || ''),
        answer: String(data.answer || ''),
        explanation: String(data.explanation || ''),
        options: Array.isArray(data.options) ? data.options.filter((o): o is string => typeof o === 'string') : undefined,
        type: data.type as StoredProblem['type'],
        subject: normalizeLiveTutorSubject(undefined),
      };
      if (!parsedProblem.question || !parsedProblem.answer) throw new Error('Generated result is incomplete. Please try again.');
      if (!await checkAndConsumeQuota()) return;
      setLiveTutorSelection({ kind: 'problem', problem: parsedProblem });
      setIsHomeUploadMenuOpen(false);
      requestLiveTutorStart();
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      const isFetchError = error instanceof TypeError || /fetch failed/i.test(msg) || /sending request/i.test(msg);
      setHomeParseErrorMessage(isFetchError ? 'Network issue. Please retry.' : (msg || 'Failed to generate question'));
      setIsHomeParseErrorOpen(true);
    } finally {
      setIsHomeParseLoading(false);
    }
  };

  useEffect(() => {
    void fetchLiveTutorProblems();
  }, [fetchLiveTutorProblems]);

  useEffect(() => {
    void fetchLiveTutorSkills();
  }, [fetchLiveTutorSkills]);

  useEffect(() => {
    void fetchAmcProblems();
  }, [fetchAmcProblems]);

  useEffect(() => {
    void fetchHistoryProblems();
  }, [fetchHistoryProblems]);

  useEffect(() => {
    void fetchScienceProblems();
  }, [fetchScienceProblems]);

  useEffect(() => {
    return () => {
      if (autoRotationResumeTimerRef.current) {
        window.clearTimeout(autoRotationResumeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncViewport = () => setLiveTutorViewportWidth(window.innerWidth);
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (homeUploadMenuRef.current && !homeUploadMenuRef.current.contains(event.target as Node)) {
        setIsHomeUploadMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleParentLiveTutorRequest = (event: Event) => {
      const customEvent = event as CustomEvent<{ selection?: DemoSelection }>;
      const incomingSelection = customEvent.detail?.selection;
      if (!incomingSelection) return;

      void (async () => {
        if (!await checkAndConsumeQuota()) return;
        setLiveTutorSelection(incomingSelection);
        setLiveTutorActiveTab(incomingSelection.kind === 'skill' ? 'skills' : 'problems');
        requestLiveTutorStart();
      })();
    };

    window.addEventListener('parent-live-tutor-request', handleParentLiveTutorRequest as EventListener);
    return () =>
      window.removeEventListener('parent-live-tutor-request', handleParentLiveTutorRequest as EventListener);
  }, [checkAndConsumeQuota, requestLiveTutorStart]);

  useEffect(() => {
    if (!isTabAutoRotating || liveTutorActiveTab === 'upload') return;
    const timer = window.setTimeout(() => {
      setLiveTutorActiveTab((currentTab) => {
        if (currentTab === 'upload') return currentTab;
        const currentIndex = AUTO_ROTATING_LIVE_TUTOR_TABS.findIndex((tab) => tab.key === currentTab);
        const nextIndex = (currentIndex + 1) % AUTO_ROTATING_LIVE_TUTOR_TABS.length;
        return AUTO_ROTATING_LIVE_TUTOR_TABS[nextIndex].key;
      });
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [isTabAutoRotating, liveTutorActiveTab]);

  useEffect(() => {
    if (liveTutorActiveTab === 'upload') return;

    const container = carouselScrollRef.current;
    const dataLength =
      liveTutorActiveTab === 'problems'
        ? liveTutorProblems.length
        : liveTutorActiveTab === 'skills'
          ? liveTutorMathSkills.length
          : liveTutorActiveTab === 'amc8'
            ? amcProblems.length
            : liveTutorActiveTab === 'history'
              ? historyProblems.length
              : scienceProblems.length;

    if (!container || dataLength === 0) return;

    let oneSetWidth = container.scrollWidth / 3;
    const measureOneSetWidth = () => {
      oneSetWidth = container.scrollWidth / 3;
    };
    const normalizeScrollLeft = (rawScrollLeft: number) => {
      if (oneSetWidth <= 0) return rawScrollLeft;
      let next = rawScrollLeft;
      while (next >= oneSetWidth * 2) next -= oneSetWidth;
      while (next < 0) next += oneSetWidth;
      return next;
    };

    if (lastCarouselTabRef.current !== liveTutorActiveTab || lastCarouselDataLenRef.current !== dataLength) {
      container.scrollLeft = oneSetWidth;
      lastCarouselTabRef.current = liveTutorActiveTab;
      lastCarouselDataLenRef.current = dataLength;
    }

    let frameId = 0;
    let lastTime = 0;
    let resizeObserver: ResizeObserver | null = null;
    const speed = liveTutorActiveTab === 'skills' || liveTutorActiveTab === 'history' ? -0.04 : 0.04;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        measureOneSetWidth();
      });
      resizeObserver.observe(container);
    }

    const animate = (time: number) => {
      if (carouselDragRef.current) {
        lastTime = 0;
        frameId = window.requestAnimationFrame(animate);
        return;
      }
      if (!lastTime) lastTime = time;
      const delta = Math.min(time - lastTime, 100);
      lastTime = time;
      container.scrollLeft = normalizeScrollLeft(container.scrollLeft + delta * speed);
      frameId = window.requestAnimationFrame(animate);
    };

    frameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
    };
  }, [liveTutorActiveTab, liveTutorProblems.length, liveTutorMathSkills.length, amcProblems.length, historyProblems.length, scienceProblems.length]);

  return (
    <section className="mx-auto w-full max-w-[940px] space-y-0">
      <div className="relative">
        <LiveWhiteboard
          selection={liveTutorSelection}
          isGuestSessionLocked={isGuestSessionLocked}
          onRequireAuth={onRequireAuth}
          onProblemCompleted={onProblemCompleted}
          onSessionEnd={onSessionEnd}
          previewEntries={PREVIEW_BOARD_ENTRIES}
          startSessionSignal={liveTutorStartSignal}
          desktopWidth={desktopWidth}
          desktopHeight={desktopHeight}
        />
      </div>

      <div className="mt-[10px] rounded-xl bg-white px-3 pt-1 pb-3">
        <div className="relative z-20 mb-3 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          {AUTO_ROTATING_LIVE_TUTOR_TABS.map((tab) => {
            const isActive = liveTutorActiveTab === tab.key;
            const TabIcon = tab.icon;
            const displayLabel = isMobileTabViewport
              ? (tab.key === 'skills' ? 'Skills' : tab.key === 'amc8' ? 'AMC-8' : tab.label)
              : tab.label;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  stopAutoRotation();
                  setLiveTutorActiveTab(tab.key);
                }}
                className={`relative flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-xs font-bold tracking-wide transition-all sm:px-4 sm:text-sm ${
                  isActive
                    ? 'text-white shadow-md'
                    : 'bg-[color:var(--surface-1)] text-[color:var(--gray-500)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--gray-700)]'
                }`}
                style={isActive ? { backgroundColor: tab.color } : undefined}
                aria-pressed={isActive}
              >
                <TabIcon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-[color:var(--gray-400)]'}`} strokeWidth={2.5} />
                {displayLabel}
              </button>
            );
          })}
          {(() => {
            const isActive = liveTutorActiveTab === UPLOAD_LIVE_TUTOR_TAB.key;
            const TabIcon = UPLOAD_LIVE_TUTOR_TAB.icon;
            return (
              <button
                type="button"
                onClick={() => setLiveTutorActiveTab(UPLOAD_LIVE_TUTOR_TAB.key)}
                className={`relative flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-xs font-bold tracking-wide transition-all sm:px-4 sm:text-sm ${
                  isActive
                    ? 'text-white shadow-md'
                    : 'bg-[color:var(--surface-1)] text-[color:var(--gray-500)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--gray-700)]'
                }`}
                style={isActive ? { backgroundColor: UPLOAD_LIVE_TUTOR_TAB.color } : undefined}
                aria-pressed={isActive}
              >
                <TabIcon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-[color:var(--gray-400)]'}`} strokeWidth={2.5} />
                {UPLOAD_LIVE_TUTOR_TAB.label}
              </button>
            );
          })()}
        </div>

        <div
          ref={carouselScrollRef}
          onMouseDown={handleCarouselDragStart}
          onMouseMove={handleCarouselDragMove}
          onMouseUp={handleCarouselDragEnd}
          onMouseLeave={handleCarouselDragEnd}
          onTouchStart={handleCarouselDragStart}
          onTouchMove={handleCarouselDragMove}
          onTouchEnd={handleCarouselDragEnd}
          className={`relative z-20 flex min-h-[80px] items-start gap-3 pb-1 [will-change:scroll-position] sm:min-h-[88px] ${
            liveTutorActiveTab === 'upload'
              ? 'overflow-visible cursor-default select-text'
              : 'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [touch-action:pan-y] select-none cursor-grab active:cursor-grabbing'
          }`}
        >
          {liveTutorActiveTab === 'problems' && (
            isLoadingProblems && liveTutorProblems.length === 0 ? (
              <div className="text-sm text-[color:var(--gray-500)]">Loading practice problems...</div>
            ) : liveTutorProblems.length === 0 ? (
              <div className="text-sm text-[color:var(--gray-500)]">No practice problems available yet.</div>
            ) : (
              [...liveTutorProblems, ...liveTutorProblems, ...liveTutorProblems].map((problem, index) => {
                const isSelected = liveTutorSelection?.kind === 'problem' && liveTutorSelection.problem.id === problem.id;
                const text = formatProblemLabel(problem.question);
                return (
                  <button
                    key={`${problem.id}-${index}`}
                    type="button"
                    onClick={() => void handleSelectLiveTutorProblem(problem.id)}
                    className={`relative flex h-[72px] min-w-[140px] max-w-[280px] flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border px-3 py-1.5 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer sm:h-[80px] sm:min-w-[160px] sm:max-w-[320px] sm:px-4 sm:py-2 ${
                      isSelected
                        ? 'border-[color:var(--primary-200)] bg-[#EDFDF6] text-[color:var(--primary-700)]'
                        : 'border-[color:var(--surface-3)] bg-[#F6FDFB] text-[color:var(--gray-700)]'
                    }`}
                    style={{ height: cardHeight }}
                  >
                    <div className="pointer-events-none w-full overflow-hidden">
                      <MarkdownComponent
                        content={text}
                        textStyle={{
                          fontSize: cardFontSize,
                          fontWeight: 400,
                          color: 'inherit',
                          textAlign: 'center',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      />
                    </div>
                  </button>
                );
              })
            )
          )}

          {liveTutorActiveTab === 'skills' && (
            isLoadingSkills ? null : liveTutorMathSkills.length === 0 ? (
              <div className="text-sm text-[color:var(--gray-500)]">No math skills available yet.</div>
            ) : (
              [...liveTutorMathSkills, ...liveTutorMathSkills, ...liveTutorMathSkills].map((skill, index) => {
                const isSelected = liveTutorSelection?.kind === 'skill' && liveTutorSelection.skillId === skill.skill_id;
                return (
                  <button
                    key={`${skill.skill_id}-${index}`}
                    type="button"
                    onClick={() => void handleSelectLiveTutorSkill(skill)}
                    className={`relative flex h-[72px] min-w-[140px] max-w-[280px] flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border px-3 py-1.5 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer sm:h-[80px] sm:min-w-[160px] sm:max-w-[320px] sm:px-4 sm:py-2 ${
                      isSelected
                        ? 'border-[color:var(--primary-200)] bg-[#FFFBF0] text-[color:var(--primary-700)]'
                        : 'border-[color:var(--surface-3)] bg-[#FFFDF7] text-[color:var(--gray-700)]'
                    }`}
                    style={{ height: cardHeight }}
                  >
                    <div className="pointer-events-none w-full overflow-hidden">
                      <MarkdownComponent
                        content={skill.title}
                        textStyle={{
                          fontSize: cardFontSize,
                          fontWeight: 400,
                          color: 'inherit',
                          textAlign: 'center',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      />
                    </div>
                  </button>
                );
              })
            )
          )}

          {liveTutorActiveTab === 'amc8' && (
            isLoadingAmcProblems && amcProblems.length === 0 ? (
              <div className="text-sm text-[color:var(--gray-500)]">Loading AMC 8 problems...</div>
            ) : amcProblems.length === 0 ? (
              <div className="text-sm text-[color:var(--gray-500)]">No AMC 8 problems available yet.</div>
            ) : (
              [...amcProblems, ...amcProblems, ...amcProblems].map((problem, index) => {
                const isSelected = liveTutorSelection?.kind === 'problem' && liveTutorSelection.problem.id === problem.id;
                const qNum = problem.id.replace('amc8-2026-q', '2026-Q');
                const text = formatProblemLabel(problem.question);
                return (
                  <button
                    key={`${problem.id}-${index}`}
                    type="button"
                    onClick={() => void handleSelectAmcProblem(problem.id)}
                    className={`relative flex h-[72px] min-w-[140px] max-w-[280px] flex-shrink-0 flex-col items-center justify-center overflow-hidden rounded-2xl border px-3 py-1.5 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer sm:h-[80px] sm:min-w-[160px] sm:max-w-[320px] sm:px-4 sm:py-2 ${
                      isSelected
                        ? 'border-blue-200 bg-[#EBF5FF] text-blue-700'
                        : 'border-[color:var(--surface-3)] bg-[#F5FAFF] text-[color:var(--gray-700)]'
                    }`}
                    style={{ height: cardHeight }}
                  >
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#3B82F6]">{qNum}</span>
                    <div className="pointer-events-none w-full overflow-hidden">
                      <MarkdownComponent
                        content={text}
                        textStyle={{
                          fontSize: cardFontSize,
                          fontWeight: 400,
                          color: 'inherit',
                          textAlign: 'center',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      />
                    </div>
                  </button>
                );
              })
            )
          )}
          {liveTutorActiveTab === 'history' && (
            isLoadingHistoryProblems && historyProblems.length === 0 ? (
              <div className="text-sm text-[color:var(--gray-500)]">Loading history problems...</div>
            ) : historyProblems.length === 0 ? (
              <div className="text-sm text-[color:var(--gray-500)]">No history problems available yet.</div>
            ) : (
              [...historyProblems, ...historyProblems, ...historyProblems].map((problem, index) => {
                const isSelected = liveTutorSelection?.kind === 'problem' && liveTutorSelection.problem.id === problem.id;
                const text = formatProblemLabel(problem.question);
                return (
                  <button
                    key={`${problem.id}-${index}`}
                    type="button"
                    onClick={() => void handleSelectHistoryProblem(problem.id)}
                    className={`relative flex h-[72px] min-w-[140px] max-w-[280px] flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border px-3 py-1.5 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer sm:h-[80px] sm:min-w-[160px] sm:max-w-[320px] sm:px-4 sm:py-2 ${
                      isSelected
                        ? 'border-[#D97706]/40 bg-[#FFF7ED] text-[#92400E]'
                        : 'border-[#D97706]/30 bg-[#FFFBEB] text-[#92600A]'
                    }`}
                    style={{ height: cardHeight }}
                  >
                    <div className="pointer-events-none w-full overflow-hidden">
                      <MarkdownComponent
                        content={text}
                        textStyle={{
                          fontSize: cardFontSize,
                          fontWeight: 400,
                          color: 'inherit',
                          textAlign: 'center',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      />
                    </div>
                  </button>
                );
              })
            )
          )}

          {liveTutorActiveTab === 'science' && (
            isLoadingScienceProblems && scienceProblems.length === 0 ? (
              <div className="text-sm text-[color:var(--gray-500)]">Loading science problems...</div>
            ) : scienceProblems.length === 0 ? (
              <div className="text-sm text-[color:var(--gray-500)]">No science problems available yet.</div>
            ) : (
              [...scienceProblems, ...scienceProblems, ...scienceProblems].map((problem, index) => {
                const isSelected = liveTutorSelection?.kind === 'problem' && liveTutorSelection.problem.id === problem.id;
                const text = formatProblemLabel(problem.question);
                return (
                  <button
                    key={`${problem.id}-${index}`}
                    type="button"
                    onClick={() => void handleSelectScienceProblem(problem.id)}
                    className={`relative flex h-[72px] min-w-[140px] max-w-[280px] flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border px-3 py-1.5 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer sm:h-[80px] sm:min-w-[160px] sm:max-w-[320px] sm:px-4 sm:py-2 ${
                      isSelected
                        ? 'border-[#06B6D4]/40 bg-[#E0F7FA] text-[#0E7490]'
                        : 'border-[#06B6D4]/30 bg-[#ECFEFF] text-[#0E7490]'
                    }`}
                    style={{ height: cardHeight }}
                  >
                    <div className="pointer-events-none w-full overflow-hidden">
                      <MarkdownComponent
                        content={text}
                        textStyle={{
                          fontSize: cardFontSize,
                          fontWeight: 400,
                          color: 'inherit',
                          textAlign: 'center',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      />
                    </div>
                  </button>
                );
              })
            )
          )}

          {liveTutorActiveTab === 'upload' && (
            <div
              className="w-full"
              style={isMobileTabViewport ? undefined : { height: cardHeight }}
            >
              <div
                className={`rounded-[24px] border border-[#D7D9E8] bg-white ${
                  isMobileTabViewport ? 'px-4 py-4' : 'flex h-full items-center px-4'
                }`}
              >
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="flex-1 rounded-xl bg-white px-2">
                    <div className="flex items-center gap-2">
                      <textarea
                        value={homeUploadText}
                        onChange={(e) => setHomeUploadText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                            e.preventDefault();
                            void handleHomeUploadSubmit();
                          }
                        }}
                        placeholder="Type your own math problem here..."
                        className={`w-full resize-none appearance-none bg-transparent px-1 text-sm text-[#48566A] outline-none ring-0 focus:border-transparent focus:outline-none focus:ring-0 focus:shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
                          isMobileTabViewport ? 'h-12 leading-6 pt-1' : 'h-10 leading-10'
                        }`}
                        style={{ outline: 'none', boxShadow: 'none', WebkitAppearance: 'none', marginLeft: -5 } as React.CSSProperties}
                      />
                    </div>
                    {homeUploadFileName && (
                      <p className="mt-1 px-1 text-xs text-[#6D7492]">Image selected: {homeUploadFileName}</p>
                    )}
                  </div>
                  <div className={`flex flex-shrink-0 items-center ${isMobileTabViewport ? 'gap-3' : 'gap-2'}`}>
                    <div className="relative flex-shrink-0" ref={homeUploadMenuRef}>
                      <button
                        type="button"
                        onClick={handleToggleHomeUploadMenu}
                        aria-label="Open image upload menu"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#F1F1FF] text-[#6D7492] transition hover:bg-[#E9E9FF]"
                      >
                        <ImagePlus className="h-5 w-5" />
                      </button>
                      {isHomeUploadMenuOpen && (
                        <div className="absolute left-0 top-12 z-20 w-44 rounded-xl border border-[#D7D9E8] bg-white p-1 shadow-[0_12px_24px_rgba(15,23,42,0.12)] sm:left-auto sm:right-0 sm:top-10">
                          <button type="button" onClick={handlePickHomeImage} className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-[#48566A] transition hover:bg-[#F6F7FF]">Upload image</button>
                          <button type="button" onClick={() => setIsHomeUploadMenuOpen(false)} className="mt-1 w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-[#48566A] transition hover:bg-[#F6F7FF]">Cancel</button>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleHomeUploadSubmit()}
                      disabled={(!homeUploadText.trim() && !homeUploadFile) || isHomeParseLoading}
                      className={`inline-flex h-10 items-center justify-center rounded-xl text-sm font-semibold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60 ${
                        isMobileTabViewport ? 'min-w-0 flex-1 px-5' : 'px-7'
                      }`}
                      style={{ backgroundColor: '#8B8CFD' }}
                    >
                      {isHomeParseLoading ? 'Generating...' : 'Upload'}
                    </button>
                  </div>
                </div>
              </div>
              {isHomeParseErrorOpen && homeParseErrorMessage && (
                <p className="mt-2 text-center text-sm text-red-500">{homeParseErrorMessage}</p>
              )}
            </div>
          )}
        </div>
        <input ref={homeUploadInputRef} type="file" accept="image/*" onChange={handleHomeUploadFileChange} className="hidden" />
      </div>
      <PaywallModal visible={showPaywall} feature="live_tutor" onClose={() => setShowPaywall(false)} />
    </section>
  );
}
